import { useState } from 'react';
import styled from 'styled-components';

import ReferralSlideout from 'components/referrals/ReferralSlideout';
import { FilledBadge } from 'components/shared/Badge';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import { InputStyles } from 'components/shared/form/Input';
import StandardModal, { Actions, ColorThemeName } from 'components/shared/StandardModal';
import ThinSearch from 'components/shared/ThinSearch';
import { ReferralProvider } from 'context/review/referral';
import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import Workflow, { BranchOption, WorkflowAction, WorkflowStatus } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall, BodySmallBold } from 'styles/typography';
import BadgeCheckIcon from 'svg/BadgeCheckIcon';
import BadgeXIcon from 'svg/BadgeXIcon';
import PencilIcon from 'svg/PencilIcon';

import { InlineLink } from './categoryCard/styles';
import {
  ASSIGNED_GROUP_ID_VALUE as GROUP_VALUE,
  DENIAL_REASON_VALUE as REASON_VALUE,
  OUTCOME_ACCEPTED,
  OUTCOME_DENIED,
  recordedOutcome,
} from './constants';
import { completionAttribution } from './FanoutPanel';

const NOTE_VALUE = 'central_intake_note';

type Reason = WorkflowAction['denialReasonOptions'][number];

const addressLine = ({ streetAddress, city, state, zip }: NonNullable<BranchOption['address']> = {}) =>
  [streetAddress, city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');

interface Props {
  action: WorkflowAction;
  workflow: Workflow;
  onComplete: (actionId: string, outputs: Record<string, string>) => unknown;
  onAmend?: (actionId: string, outputs: Record<string, string>) => unknown;
  // Intake's change of mind after accepting: a value write of the branch, which the API moves the access and the
  // sender's referral along with.
  onUpdateValues?: (values: Record<string, string>) => Promise<unknown>;
  isCompleting: boolean;
}

// Central intake's decision in the lighter Inbound Referrals (CentralIntakeDecision): pick ONE of the client's
// branches and Accept, or Deny with the sender's reason: a denial names the branch too. The list is the Facility Decision
// panel's, narrowed to a single selection with one decision for the whole referral. The API offers the branches and
// the reasons on the step, and tells the sender; the internal note stays with the provider.
export default function CentralIntakeDecisionPanel({
  action,
  workflow,
  onComplete,
  onAmend,
  onUpdateValues,
  isCompleting,
}: Props) {
  const { canEditAction } = useWorkflowPermissions();
  const [selectedId, setSelectedId] = useState('');
  const [denying, setDenying] = useState(false);

  if (action.isCompleted) {
    return (
      <CentralIntakeDecisionResult
        action={action}
        workflow={workflow}
        onAmend={onAmend}
        onUpdateValues={onUpdateValues}
        isCompleting={isCompleting}
      />
    );
  }

  const editable = canEditAction(action, workflow);

  return (
    <Panel data-testid='central-intake-decision-panel'>
      <Intro>
        Select a branch below and provide an accept or deny decision for this referral.{' '}
        {workflow.sourceReviewId && <ReferralDetailsLink referralId={workflow.sourceReviewId} />}
      </Intro>

      <BranchList
        options={action.branchOptions}
        selectedId={selectedId}
        disabled={!editable}
        onSelect={setSelectedId}
      />

      {editable && (
        <Footer>
          {!selectedId && <Hint>A branch must first be selected</Hint>}
          <Buttons>
            <Button
              variant='danger'
              disabled={isCompleting || !selectedId}
              onClick={() => setDenying(true)}
              data-testid='decision-deny'>
              Deny
            </Button>
            <Button
              variant='call-to-action'
              disabled={isCompleting || !selectedId}
              onClick={() => onComplete(action.id, { final_outcome: OUTCOME_ACCEPTED, [GROUP_VALUE]: selectedId })}
              data-testid='decision-accept'>
              Accept
            </Button>
          </Buttons>
        </Footer>
      )}

      {denying && (
        <DenyModal
          isCompleting={isCompleting}
          reasons={action.denialReasonOptions}
          onCancel={() => setDenying(false)}
          onConfirm={(reason, note) => {
            setDenying(false);
            onComplete(action.id, {
              final_outcome: OUTCOME_DENIED,
              [GROUP_VALUE]: selectedId,
              [REASON_VALUE]: reason,
              [NOTE_VALUE]: note,
            });
          }}
        />
      )}
    </Panel>
  );
}

// The client's branches with their addresses, one picked at a time; a search above once there are more than fit.
function BranchList({
  options,
  selectedId,
  disabled,
  onSelect,
}: {
  options: BranchOption[];
  selectedId: string;
  disabled: boolean;
  onSelect: (id: string) => void;
}) {
  const [search, setSearch] = useState('');
  const term = search.trim().toLowerCase();
  const visible = term ? options.filter((option) => option.name.toLowerCase().includes(term)) : options;

  return (
    <>
      {options.length > 4 && (
        <Toolbar>
          <SearchWrap>
            <ThinSearch value={search} onChange={setSearch} placeholder='Search for Branch by Name' />
          </SearchWrap>
        </Toolbar>
      )}

      <RowList $scroll={visible.length > 4}>
        {visible.map((option) => (
          <Row key={option.id} data-testid='decision-branch-row'>
            <Radio
              type='radio'
              name='decision-branch'
              checked={selectedId === option.id}
              disabled={disabled}
              onChange={() => onSelect(option.id)}
              data-testid={`decision-branch-${option.id}`}
            />
            <div>
              <RowName>{option.name}</RowName>
              {option.address && <RowAddress>{addressLine(option.address)}</RowAddress>}
            </div>
          </Row>
        ))}
        {visible.length === 0 && <Empty>No branches to send to.</Empty>}
      </RowList>
    </>
  );
}

// The answer: Accepted and where it was sent, or Denied and why, who decided, and intake's note. An accepted referral
// can still be changed to Denied, keeping its branch, or moved to another branch in place.
export function CentralIntakeDecisionResult({
  action,
  workflow,
  onAmend,
  onUpdateValues,
  isCompleting = false,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  onAmend?: (actionId: string, outputs: Record<string, string>) => unknown;
  onUpdateValues?: (values: Record<string, string>) => Promise<unknown>;
  isCompleting?: boolean;
}) {
  const { canEditAction } = useWorkflowPermissions();
  const [denying, setDenying] = useState(false);
  const [editingBranch, setEditingBranch] = useState(false);
  const [pickedId, setPickedId] = useState('');
  const accepted = recordedOutcome(workflow) === OUTCOME_ACCEPTED;
  const branchId = workflow.valueFor(GROUP_VALUE)?.value;
  const branchName = [...workflow.groups, ...action.branchOptions].find((group) => group.id === branchId)?.name;
  const reasonValue = workflow.valueFor(REASON_VALUE)?.value;
  const reason = action.denialReasonOptions.find((option) => option.apiName === reasonValue)?.displayName;
  const note = workflow.valueFor(NOTE_VALUE)?.value;
  const attribution = completionAttribution(action);
  const changeable =
    accepted && canEditAction(action, workflow) && workflow.status !== WorkflowStatus.CANCELED && !workflow.isLocked;
  const canChangeToDenied = changeable && !!onAmend;
  const canChangeBranch = changeable && !!onUpdateValues;

  const summary = accepted
    ? `Sent to ${branchName ?? 'another branch'}`
    : `Denied${branchName ? ` at ${branchName}` : ''}${reason ? ` — ${reason}` : ''}`;

  if (editingBranch && onUpdateValues) {
    return (
      <Panel data-testid='central-intake-decision-result'>
        <BranchList options={action.branchOptions} selectedId={pickedId} disabled={false} onSelect={setPickedId} />
        <Footer>
          <Buttons>
            <Button variant='ghost' onClick={() => setEditingBranch(false)}>
              Cancel
            </Button>
            <Button
              disabled={isCompleting || !pickedId || pickedId === branchId}
              onClick={() => onUpdateValues({ [GROUP_VALUE]: pickedId }).then(() => setEditingBranch(false))}
              data-testid='decision-branch-save'>
              Save
            </Button>
          </Buttons>
        </Footer>
      </Panel>
    );
  }

  return (
    <Result data-testid='central-intake-decision-result'>
      <FilledBadge color={accepted ? 'green' : 'red'} icon={accepted ? <BadgeCheckIcon /> : <BadgeXIcon />}>
        {accepted ? 'Accepted' : 'Denied'}
      </FilledBadge>
      <Summary>
        <BodySmall data-testid='decision-result-summary'>{summary}</BodySmall>
        {canChangeBranch && (
          <PencilButton
            type='button'
            aria-label='Change branch'
            data-testid='decision-change-branch'
            onClick={() => {
              setPickedId(typeof branchId === 'string' ? branchId : '');
              setEditingBranch(true);
            }}>
            <PencilIcon color={colors.blue100} width={12} height={12} />
          </PencilButton>
        )}
      </Summary>
      {!accepted && typeof note === 'string' && note && (
        <BodySmall data-testid='decision-result-note'>Internal note: {note}</BodySmall>
      )}
      {attribution && <Muted>{attribution}</Muted>}
      {workflow.sourceReviewId && (
        <BodySmall>
          <ReferralDetailsLink referralId={workflow.sourceReviewId} />
        </BodySmall>
      )}
      {canChangeToDenied && (
        <Button
          variant='outline-danger'
          size='small'
          disabled={isCompleting}
          onClick={() => setDenying(true)}
          data-testid='decision-change-to-denied'>
          Change to Denied
        </Button>
      )}
      {denying && onAmend && (
        <DenyModal
          isCompleting={isCompleting}
          reasons={action.denialReasonOptions}
          onCancel={() => setDenying(false)}
          onConfirm={(reasonName, noteText) => {
            setDenying(false);
            onAmend(action.id, { final_outcome: OUTCOME_DENIED, [REASON_VALUE]: reasonName, [NOTE_VALUE]: noteText });
          }}
        />
      )}
    </Result>
  );
}

// The SNF's referral, opened over the page from the step before and after the decision. The slideout is fixed, so it
// can mount wherever the link does.
function ReferralDetailsLink({ referralId }: { referralId: string }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <InlineLink type='button' onClick={() => setOpen(true)} data-testid='view-referral-details'>
        View more referral details
      </InlineLink>
      {open && (
        <ReferralProvider referralId={referralId}>
          <ReferralSlideout onClose={() => setOpen(false)} />
        </ReferralProvider>
      )}
    </>
  );
}

function DenyModal({
  isCompleting,
  reasons,
  onCancel,
  onConfirm,
}: {
  isCompleting: boolean;
  reasons: Reason[];
  onCancel: () => void;
  onConfirm: (reason: string, note: string) => void;
}) {
  const [reason, setReason] = useState<Reason | null>(null);
  const [note, setNote] = useState('');

  return (
    <StandardModal title='Deny Referral' onCancel={onCancel} colorTheme={ColorThemeName.PrimaryRed}>
      <DenyForm>
        <div>
          <FieldLabel name='decision-denial-reason'>Denial Reason</FieldLabel>
          <Select<Reason>
            inputId='decision-denial-reason'
            placeholder='Select a reason'
            options={reasons}
            value={reason}
            onChange={setReason}
            getOptionValue={(option) => option.apiName}
            getOptionLabel={(option) => option.displayName}
          />
        </div>
        <div>
          <FieldLabel name='decision-note'>Internal note (optional)</FieldLabel>
          <NoteInput
            id='decision-note'
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder='Only your team sees this; the referring facility never does'
            data-testid='decision-note'
          />
        </div>
      </DenyForm>
      <Actions>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant='danger'
          disabled={isCompleting || !reason}
          onClick={() => reason && onConfirm(reason.apiName, note.trim())}
          data-testid='decision-deny-confirm'>
          Deny Referral
        </Button>
      </Actions>
    </StandardModal>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
`;

const Intro = styled(Body)`
  padding: 16px 16px 4px;
`;

const Toolbar = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${colors.black5};
`;

const SearchWrap = styled.div`
  max-width: 360px;
`;

const RowList = styled.div<{ $scroll: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ $scroll }) =>
    $scroll &&
    `
      max-height: 296px;
      overflow-y: auto;
    `}
`;

// A label, so the name and address pick the branch too.
const Row = styled.label`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  cursor: pointer;
`;

const Radio = styled.input`
  width: 20px;
  height: 20px;
  margin: 0;
  flex-shrink: 0;
  accent-color: ${colors.blue100};
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

const RowName = styled(BodySmallBold)`
  font-size: 14px;
`;

const RowAddress = styled(BodySmall)`
  color: ${colors.black50};
`;

const Empty = styled(BodySmall)`
  padding: 14px 16px;
  color: ${colors.black50};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-top: 1px solid ${colors.black10};
`;

const Hint = styled(Body)`
  color: ${colors.black50};
`;

const Buttons = styled(ButtonGroup)`
  margin-left: auto;
`;

const Result = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const Summary = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PencilButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
`;

const Muted = styled(BodySmall)`
  color: ${colors.black50};
`;

// Not the shared ModalContent: that centers a confirm's copy, and this is a form.
const DenyForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 20px 0 24px;
`;

const NoteInput = styled.textarea`
  ${InputStyles}
  min-height: 96px;
  resize: vertical;
`;
